import { useState, useEffect } from "react";
import { Routes, Route, Navigate, useParams } from "react-router-dom";
import LoginPage from "./components/LoginPage";
import Layout from "./components/Layout";
import KnowledgeBasePage from "./components/KnowledgeBasePage";
import TenantsPage from "./components/TenantsPage";
import TenantEditPage from "./components/TenantEditPage";
import TenantCreatePage from "./components/TenantCreatePage";
import OpsPage from "./components/OpsPage";
import ChatLogsPage from "./components/ChatLogsPage";
import KbLearningPage from "./components/KbLearningPage";
import LeadsPage from "./components/LeadsPage";
import TemplatesPage from "./components/TemplatesPage";
import MarketingAssetsPage from "./components/MarketingAssetsPage";
import ReferralsPage from "./components/ReferralsPage";
import PartnersPage from "./components/PartnersPage";
import TrackingPage from "./components/TrackingPage";
import SystemInfoPage from "./components/SystemInfoPage";
import EngineRulesPage from "./components/EngineRulesPage";
import AntiAbusePage from "./components/AntiAbusePage";
import EshopSitesPage from "./components/EshopSitesPage";
import OnboardingPage from "./components/OnboardingPage";
import VoiceAssistantPage from "./components/VoiceAssistantPage";
import AnalystPage from "./components/AnalystPage";
import OutboundPage from "./components/OutboundPage";
import CommercePage from "./components/CommercePage";

// 舊「功能模塊」獨立頁已併入編輯頁 tab（2026-09-11）；保留路由做 redirect 免舊 bookmark 死link
function FeaturesRedirect() {
  const { tenantId } = useParams<{ tenantId: string }>();
  return <Navigate to={`/tenants/edit/${tenantId}?tab=features`} replace />;
}

// IA 四組收斂（2026-10-19 方案 §6）：會議內容屬租戶級操作，搬去 /merchant「AI 秘書 → 秘書日誌」；
// 舊路由保留說明頁，唔刪唔隱藏——platform 排查請用語音服務運維台。
function VoiceNotesMoved() {
  return (
    <div className="max-w-xl p-6 bg-slate-900 border border-slate-800 rounded-xl">
      <h1 className="text-xl font-bold text-slate-100 mb-3">🎙️ 語音會議記錄已搬遷</h1>
      <p className="text-sm text-slate-400 leading-relaxed mb-3">
        會議記錄屬租戶自己的內容，已由 /admin 搬去商戶門戶：<span className="text-slate-200">/merchant → AI 秘書 → 秘書日誌</span>（租戶登入後可見）。
      </p>
      <p className="text-sm text-slate-400 leading-relaxed">
        平台排查語音服務（簽發通行證、租戶開關、情報同步健康）請用「🛡️ 品質與風控 → 🎙️ 語音服務運維台」。
      </p>
    </div>
  );
}

export default function App() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  useEffect(() => {
    const key = localStorage.getItem("admin_key");
    if (key && key.length > 0) {
      setIsLoggedIn(true);
    }
    /* 2026-10-27：401 改事件通知（原 location.reload 令舊金鑰用戶無限閃載／重複登入
       登出循環）——任何 API 收 401＝即時清 key 切登入頁，零頁面重載，輪詢隨卸載終止 */
    const onUnauthorized = () => setIsLoggedIn(false);
    window.addEventListener("admin-unauthorized", onUnauthorized);
    return () => window.removeEventListener("admin-unauthorized", onUnauthorized);
  }, []);

  const handleLogin = (key: string) => {
    localStorage.setItem("admin_key", key);
    setIsLoggedIn(true);
  };

  const handleLogout = () => {
    localStorage.removeItem("admin_key");
    setIsLoggedIn(false);
  };

  if (!isLoggedIn) {
    return <LoginPage onLogin={handleLogin} />;
  }

  return (
    <Layout onLogout={handleLogout}>
      <Routes>
        <Route path="/" element={<TenantsPage />} />
        <Route path="/tenants" element={<TenantsPage />} />
        <Route path="/onboarding" element={<OnboardingPage />} />
        <Route path="/knowledge" element={<KnowledgeBasePage />} />
        <Route path="/tenants/create" element={<TenantCreatePage />} />
        <Route path="/tenants/edit/:tenantId" element={<TenantEditPage />} />
        <Route path="/tenants/features/:tenantId" element={<FeaturesRedirect />} />
        <Route path="/quota" element={<Navigate to="/tenants" replace />} />
        <Route path="/voice-notes" element={<VoiceNotesMoved />} />
        <Route path="/voice-assistant" element={<VoiceAssistantPage />} />
        <Route path="/analyst" element={<AnalystPage />} />
        <Route path="/ops" element={<OpsPage />} />
        <Route path="/tracking" element={<TrackingPage />} />
        <Route path="/chat-logs" element={<ChatLogsPage />} />
        <Route path="/kb-learning" element={<KbLearningPage />} />
        <Route path="/leads" element={<LeadsPage />} />
        <Route path="/templates" element={<TemplatesPage />} />
        <Route path="/marketing" element={<Navigate to="/marketing-assets" replace />} />
        <Route path="/marketing-assets" element={<MarketingAssetsPage />} />
        <Route path="/referrals" element={<ReferralsPage />} />
        <Route path="/commerce" element={<CommercePage />} />
        <Route path="/partners" element={<PartnersPage />} />
        <Route path="/eshop" element={<EshopSitesPage />} />
        <Route path="/system" element={<SystemInfoPage />} />
        <Route path="/outbound" element={<OutboundPage />} />
        <Route path="/engine-rules" element={<EngineRulesPage />} />
        <Route path="/anti-abuse" element={<AntiAbusePage />} />
        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
    </Layout>
  );
}
